import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { useAuth } from '@/features/auth/store'
import { ApplicationFormDialog } from '@/features/recruitment/ApplicationFormDialog'
import { StageChangeDialog } from '@/features/recruitment/StageChangeDialog'
import {
  ALLOWED_STAGE_TRANSITIONS,
  APPLICATION_STAGES,
  APPLICATION_STAGE_LABELS,
  exportApplications,
  listApplications,
  type ApplicationFilters,
  type ApplicationRead,
  type ApplicationStage,
} from '@/features/recruitment/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STAGE_TONE: Record<ApplicationStage, string> = {
  applied: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  screening: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  interview: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
  offer: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
  hired: 'bg-success/10 text-success',
  rejected: 'bg-danger/10 text-danger',
}

/**
 * The pipeline: every open and closed application, one row each, with the one legal next
 * move for its stage a click away. A table rather than a kanban board -- same idiom as
 * `PerformanceReviews` -- since sorting and export matter here as much as the shape of
 * the funnel does.
 */
export function Applications() {
  const can = useAuth((s) => s.can)
  const [filters, setFilters] = useState<ApplicationFilters>({ page: 1 })
  const [opening, setOpening] = useState(false)
  const [moving, setMoving] = useState<ApplicationRead | null>(null)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['applications', filters],
    queryFn: () => listApplications(filters),
    retry: false,
  })

  const canCreate = can('performance_reviews', 'create')
  const canUpdate = can('performance_reviews', 'update')

  const applications = data?.data ?? []
  const meta = data?.meta

  const set = (patch: Partial<ApplicationFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  const columns: Column<ApplicationRead>[] = [
    {
      key: 'candidate_name',
      header: 'Candidate',
      className: 'font-medium',
      sortValue: (application) => application.candidate_name ?? '',
      cell: (application) => (
        <Link to={`/recruitment/applications/${application.id}`} className="hover:underline">
          {application.candidate_name ?? 'Unnamed'}
        </Link>
      ),
    },
    {
      key: 'job_posting_title',
      header: 'Job posting',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (application) => application.job_posting_title ?? '—',
    },
    {
      key: 'stage',
      header: 'Stage',
      cell: (application) => (
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${STAGE_TONE[application.stage]}`}>
          {APPLICATION_STAGE_LABELS[application.stage]}
        </span>
      ),
    },
    {
      key: 'current_stage_since',
      header: 'Since',
      className: 'whitespace-nowrap text-slate-500 dark:text-slate-400',
      sortValue: (application) => application.current_stage_since,
      cell: (application) => new Date(application.current_stage_since).toLocaleDateString(),
    },
    ...(canUpdate
      ? [
          {
            key: 'actions',
            header: '',
            headerClassName: 'text-right',
            className: 'text-right',
            cell: (application: ApplicationRead) =>
              ALLOWED_STAGE_TRANSITIONS[application.stage].length > 0 ? (
                <Button variant="ghost" size="sm" onClick={() => setMoving(application)}>
                  Move
                </Button>
              ) : null,
          } satisfies Column<ApplicationRead>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Applications</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} application${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {canCreate && <Button onClick={() => setOpening(true)}>Open an application</Button>}
      </header>

      <Card>
        <CardHeader title="Pipeline" />
        <DataTable
          rows={applications}
          columns={columns}
          getRowId={(application) => application.id}
          caption="Applications"
          isLoading={isLoading}
          error={error}
          emptyMessage="No applications match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="application-stage">
                Stage
              </label>
              <select
                id="application-stage"
                className={SELECT_CLASS}
                value={filters.stage ?? ''}
                onChange={(event) => set({ stage: event.target.value as ApplicationStage | '' })}
              >
                <option value="">Every stage</option>
                {APPLICATION_STAGES.map((stage) => (
                  <option key={stage} value={stage}>
                    {APPLICATION_STAGE_LABELS[stage]}
                  </option>
                ))}
              </select>

              {filters.stage && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ page: 1 })}>
                  Clear
                </Button>
              )}

              <ExportButton
                filename="applications"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportApplications(filters, format)}
                errorMessage="Could not export the pipeline."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>

      <ApplicationFormDialog open={opening} onClose={() => setOpening(false)} />
      <StageChangeDialog
        open={moving !== null}
        application={moving ?? undefined}
        onClose={() => setMoving(null)}
      />
    </div>
  )
}
